import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  itemsPerPage = 10,
  totalItems = 0,
  showItemsInfo = true,
  className = '',
}) => {
  const startItem = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      if (currentPage <= 3) end = 4;
      if (currentPage >= totalPages - 2) start = totalPages - 3;
      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) onPageChange(page);
  };

  if (totalPages <= 1) return null;

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
      {showItemsInfo && totalItems > 0 && (
        <p className="text-sm text-neutral-500">
          Showing <span className="font-medium text-neutral-700">{startItem}</span> to{' '}
          <span className="font-medium text-neutral-700">{endItem}</span> of{' '}
          <span className="font-medium text-neutral-700">{totalItems}</span> results
        </p>
      )}

      <nav className="flex items-center gap-1">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`p-2 rounded-xl text-sm font-medium transition-all duration-200 ${
            currentPage === 1
              ? 'text-neutral-300 cursor-not-allowed'
              : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
          }`}
          aria-label="Previous page"
        >
          <ChevronLeft size={18} strokeWidth={2} />
        </button>

        <div className="flex items-center gap-1">
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-2 py-1 text-neutral-400 text-sm">...</span>
              ) : (
                <button
                  onClick={() => handlePageChange(page)}
                  className={`min-w-[36px] h-9 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    page === currentPage
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800'
                  }`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-xl text-sm font-medium transition-all duration-200 ${
            currentPage === totalPages
              ? 'text-neutral-300 cursor-not-allowed'
              : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
          }`}
          aria-label="Next page"
        >
          <ChevronRight size={18} strokeWidth={2} />
        </button>
      </nav>
    </div>
  );
};

export default Pagination;
